import React, { useState, useEffect, useRef, useCallback } from 'react';
import { supportAPI } from '../services/api';
import { FiRefreshCw, FiSend, FiCheck, FiCheckCircle, FiAlertCircle, FiMessageSquare, FiUser } from 'react-icons/fi';

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatTime(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const now = new Date();
    const today = now.toDateString() === d.toDateString();
    const timeStr = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    if (today) return timeStr;
    return `${d.getDate()}/${d.getMonth() + 1} ${timeStr}`;
  } catch {
    return '';
  }
}

function relativeTime(iso) {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1)  return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)  return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

// ── Component ─────────────────────────────────────────────────────────────────

const SupportInbox = () => {
  const [tickets, setTickets]         = useState([]);
  const [activeTicket, setActiveTicket] = useState(null);
  const [activeMessages, setActiveMessages] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [threadLoading, setThreadLoading] = useState(false);
  const [replyText, setReplyText]     = useState('');
  const [sending, setSending]         = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [error, setError]             = useState(null);
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [actionError, setActionError] = useState(null);

  const messagesEndRef  = useRef(null);
  const pollRef         = useRef(null);
  const activeTicketRef = useRef(null);
  activeTicketRef.current = activeTicket;

  // ── Data loading ─────────────────────────────────────────────────────────

  const loadTickets = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const params = statusFilter ? { status: statusFilter } : {};
      const res = await supportAPI.listTickets(params);
      setTickets(res.data || []);
      setError(null);
    } catch (e) {
      if (!silent) setError('Failed to load tickets.');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [statusFilter]);

  const loadThread = useCallback(async (ticketId, silent = false) => {
    if (!silent) setThreadLoading(true);
    try {
      const res = await supportAPI.getTicket(ticketId);
      const data = res.data;
      setActiveMessages(data.messages || []);
      // Update the ticket status in the list
      setTickets(prev => prev.map(t => t.id === ticketId ? { ...t, status: data.status, unread_by_support: 0 } : t));
      // Mark student msgs as read on the server
      await supportAPI.markRead(ticketId).catch(() => {});
    } catch (e) {
      if (!silent) console.error('Failed to load thread', e);
    } finally {
      if (!silent) setThreadLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    loadTickets();
  }, [loadTickets]);

  // Poll every 10s
  useEffect(() => {
    pollRef.current = setInterval(async () => {
      await loadTickets(true);
      if (activeTicketRef.current) {
        await loadThread(activeTicketRef.current.id, true);
      }
    }, 10000);
    return () => clearInterval(pollRef.current);
  }, [loadTickets, loadThread]);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [activeMessages]);

  // Auto-dismiss action error banners so they don't linger indefinitely
  useEffect(() => {
    if (!actionError) return;
    const t = setTimeout(() => setActionError(null), 5000);
    return () => clearTimeout(t);
  }, [actionError]);

  // ── Actions ───────────────────────────────────────────────────────────────

  const selectTicket = async (ticket) => {
    setActiveTicket(ticket);
    setActiveMessages([]);
    await loadThread(ticket.id);
  };

  const handleReply = async () => {
    if (!replyText.trim() || sending || !activeTicket) return;
    setSending(true);
    setActionError(null);
    const body = replyText.trim();
    setReplyText('');
    try {
      const res = await supportAPI.reply(activeTicket.id, body);
      setActiveMessages(prev => [...prev, res.data]);
      setTickets(prev => prev.map(t =>
        t.id === activeTicket.id
          ? { ...t, last_message: { body, sender_is_support: true, created_at: new Date().toISOString() }, updated_at: new Date().toISOString() }
          : t
      ));
    } catch {
      setReplyText(body); // restore on error
      setActionError('Message failed to send. Please try again.');
    } finally {
      setSending(false);
    }
  };

  // Local state only updates after the server confirms the change, so the
  // UI can never show "closed"/"open" while the server disagrees (MS-1).
  const handleClose = async () => {
    if (!activeTicket || statusUpdating) return;
    setStatusUpdating(true);
    setActionError(null);
    try {
      await supportAPI.close(activeTicket.id);
      setActiveTicket(t => ({ ...t, status: 'closed' }));
      setTickets(prev => prev.map(t => t.id === activeTicket.id ? { ...t, status: 'closed' } : t));
    } catch {
      setActionError('Failed to close ticket. Please try again.');
    } finally {
      setStatusUpdating(false);
    }
  };

  const handleReopen = async () => {
    if (!activeTicket || statusUpdating) return;
    setStatusUpdating(true);
    setActionError(null);
    try {
      await supportAPI.reopen(activeTicket.id);
      setActiveTicket(t => ({ ...t, status: 'open' }));
      setTickets(prev => prev.map(t => t.id === activeTicket.id ? { ...t, status: 'open' } : t));
    } catch {
      setActionError('Failed to reopen ticket. Please try again.');
    } finally {
      setStatusUpdating(false);
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      handleReply();
    }
  };

  // ── Render ────────────────────────────────────────────────────────────────

  const totalUnread = tickets.reduce((sum, t) => sum + (t.unread_by_support || 0), 0);

  return (
    <div style={styles.root}>

        {/* ── Left panel: ticket list ── */}
        <aside style={styles.sidebar}>
          <div style={styles.sidebarHeader}>
            <div>
              <h2 style={styles.sidebarTitle}>
                Support Inbox
                {totalUnread > 0 && (
                  <span style={styles.unreadBadge}>{totalUnread}</span>
                )}
              </h2>
              <p style={styles.sidebarSub}>Student in-app messages</p>
            </div>
            <button style={styles.iconBtn} onClick={() => loadTickets()} title="Refresh">
              <FiRefreshCw size={16} />
            </button>
          </div>

          {/* Filter tabs */}
          <div style={styles.filterRow}>
            {['', 'open', 'closed'].map(f => (
              <button
                key={f}
                style={{ ...styles.filterTab, ...(statusFilter === f ? styles.filterTabActive : {}) }}
                onClick={() => setStatusFilter(f)}
              >
                {f === '' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>

          {/* Ticket list */}
          <div style={styles.ticketList}>
            {loading ? (
              <div style={styles.centered}>Loading…</div>
            ) : error ? (
              <div style={styles.centered}><FiAlertCircle color="#f87171" size={24} /><br />{error}</div>
            ) : tickets.length === 0 ? (
              <div style={styles.centered}>
                <FiMessageSquare size={32} color="#4b5563" />
                <p style={{ color: '#6b7280', marginTop: 12 }}>No tickets yet</p>
              </div>
            ) : (
              tickets.map(ticket => (
                <button
                  key={ticket.id}
                  style={{
                    ...styles.ticketRow,
                    ...(activeTicket?.id === ticket.id ? styles.ticketRowActive : {}),
                  }}
                  onClick={() => selectTicket(ticket)}
                >
                  <div style={styles.avatarCircle}>
                    <FiUser size={16} color="#9ca3af" />
                  </div>
                  <div style={styles.ticketInfo}>
                    <div style={styles.ticketName}>
                      {ticket.student_name}
                      {ticket.unread_by_support > 0 && (
                        <span style={styles.unreadDot}>{ticket.unread_by_support}</span>
                      )}
                    </div>
                    <div style={styles.ticketPreview}>
                      {ticket.last_message
                        ? (ticket.last_message.sender_is_support ? 'You: ' : '') + ticket.last_message.body
                        : 'No messages yet'}
                    </div>
                  </div>
                  <div style={styles.ticketMeta}>
                    <span style={{
                      ...styles.statusPill,
                      ...(ticket.status === 'open' ? styles.statusOpen : styles.statusClosed),
                    }}>
                      {ticket.status}
                    </span>
                    <span style={styles.ticketTime}>
                      {relativeTime(ticket.updated_at)}
                    </span>
                  </div>
                </button>
              ))
            )}
          </div>
        </aside>

        {/* ── Right panel: thread ── */}
        <main style={styles.thread}>
          {!activeTicket ? (
            <div style={styles.noThread}>
              <FiMessageSquare size={48} color="#374151" />
              <p style={{ color: '#6b7280', marginTop: 16, fontSize: 15 }}>
                Select a conversation to view
              </p>
            </div>
          ) : (
            <>
              {/* Thread header */}
              <div style={styles.threadHeader}>
                <div>
                  <div style={styles.threadTitle}>{activeTicket.student_name}</div>
                  <div style={styles.threadSub}>
                    ID: {activeTicket.student_vec_id || activeTicket.student_db_id} ·{' '}
                    <span style={activeTicket.status === 'open' ? { color: '#22c55e' } : { color: '#f87171' }}>
                      {activeTicket.status}
                    </span>
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  {activeTicket.status === 'open' ? (
                    <button
                      style={{ ...styles.actionBtn, ...styles.actionBtnDanger, ...(statusUpdating ? styles.actionBtnDisabled : {}) }}
                      onClick={handleClose}
                      disabled={statusUpdating}
                    >
                      <FiCheckCircle size={14} /> {statusUpdating ? 'Closing…' : 'Close ticket'}
                    </button>
                  ) : (
                    <button
                      style={{ ...styles.actionBtn, ...styles.actionBtnSuccess, ...(statusUpdating ? styles.actionBtnDisabled : {}) }}
                      onClick={handleReopen}
                      disabled={statusUpdating}
                    >
                      <FiCheck size={14} /> {statusUpdating ? 'Reopening…' : 'Reopen'}
                    </button>
                  )}
                </div>
              </div>

              {/* Action error banner — surfaces failed close/reopen/reply instead
                  of leaving the UI in a state the server never confirmed. */}
              {actionError && (
                <div style={styles.actionErrorBanner}>
                  <FiAlertCircle size={14} />
                  <span>{actionError}</span>
                </div>
              )}

              {/* Messages area */}
              <div style={styles.messages}>
                {threadLoading ? (
                  <div style={styles.centered}>Loading conversation…</div>
                ) : activeMessages.length === 0 ? (
                  <div style={styles.centered}>
                    <p style={{ color: '#6b7280' }}>No messages yet. Wait for the student to start the conversation.</p>
                  </div>
                ) : (
                  activeMessages.map(msg => {
                    const isSupport = msg.sender_is_support;
                    return (
                      <div
                        key={msg.id}
                        style={{ ...styles.bubble, ...(isSupport ? styles.bubbleRight : styles.bubbleLeft) }}
                      >
                        {!isSupport && (
                          <div style={styles.bubbleSenderLabel}>{activeTicket.student_name}</div>
                        )}
                        {isSupport && msg.sender_name && (
                          <div style={{ ...styles.bubbleSenderLabel, textAlign: 'right' }}>
                            {msg.sender_name}
                          </div>
                        )}
                        <div style={{
                          ...styles.bubbleBubble,
                          ...(isSupport ? styles.bubbleBlue : styles.bubbleDark),
                        }}>
                          {msg.body}
                        </div>
                        <div style={{
                          ...styles.bubbleTime,
                          ...(isSupport ? { textAlign: 'right' } : {}),
                        }}>
                          {formatTime(msg.created_at)}
                          {isSupport && msg.read_at && (
                            <span style={{ marginLeft: 4, color: '#60a5fa' }}>✓✓</span>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
                <div ref={messagesEndRef} />
              </div>

              {/* Reply input */}
              {activeTicket.status === 'open' ? (
                <div style={styles.replyBar}>
                  <textarea
                    style={styles.replyInput}
                    placeholder="Type your reply… (Ctrl+Enter to send)"
                    value={replyText}
                    onChange={e => setReplyText(e.target.value)}
                    onKeyDown={onKeyDown}
                    rows={3}
                  />
                  <button
                    style={{ ...styles.sendBtn, ...(sending ? styles.sendBtnDisabled : {}) }}
                    onClick={handleReply}
                    disabled={sending || !replyText.trim()}
                  >
                    {sending ? '…' : <FiSend size={18} />}
                  </button>
                </div>
              ) : (
                <div style={styles.closedBar}>
                  <FiCheckCircle size={16} />
                  &nbsp; This ticket is closed. Reopen it to reply.
                </div>
              )}
            </>
          )}
        </main>
      </div>
  );
};

// ── Styles ────────────────────────────────────────────────────────────────────

const styles = {
  root: {
    display: 'flex',
    height: 'calc(100vh - 80px)',
    background: '#fff',
    borderRadius: 12,
    overflow: 'hidden',
    border: '1px solid #e2e8f0',
  },

  // Left panel
  sidebar: {
    width: 320,
    minWidth: 280,
    borderRight: '1px solid #e2e8f0',
    display: 'flex',
    flexDirection: 'column',
    background: '#fff',
  },
  sidebarHeader: {
    padding: '18px 16px 12px',
    borderBottom: '1px solid #e2e8f0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  sidebarTitle: {
    margin: 0,
    fontSize: 17,
    fontWeight: 700,
    color: '#1e293b',
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  sidebarSub: {
    margin: '4px 0 0',
    fontSize: 12,
    color: '#94a3b8',
  },
  unreadBadge: {
    background: '#0118c2',
    color: '#fff',
    borderRadius: 100,
    padding: '1px 7px',
    fontSize: 11,
    fontWeight: 600,
  },
  filterRow: {
    display: 'flex',
    gap: 6,
    padding: '10px 14px',
    borderBottom: '1px solid #e2e8f0',
  },
  filterTab: {
    flex: 1,
    padding: '5px 0',
    borderRadius: 8,
    border: '1px solid #e2e8f0',
    background: 'transparent',
    color: '#64748b',
    fontSize: 12,
    cursor: 'pointer',
  },
  filterTabActive: {
    background: '#0118c2',
    color: '#fff',
    borderColor: '#0118c2',
  },
  ticketList: {
    flex: 1,
    overflowY: 'auto',
  },
  ticketRow: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    padding: '12px 14px',
    gap: 10,
    background: 'transparent',
    border: 'none',
    borderBottom: '1px solid #f1f5f9',
    cursor: 'pointer',
    textAlign: 'left',
  },
  ticketRowActive: {
    background: '#f5f7ff',
    borderLeft: '3px solid #0118c2',
  },
  avatarCircle: {
    width: 38,
    height: 38,
    borderRadius: '50%',
    background: '#f1f5f9',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  ticketInfo: {
    flex: 1,
    minWidth: 0,
  },
  ticketName: {
    color: '#1e293b',
    fontSize: 13,
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    gap: 6,
  },
  ticketPreview: {
    color: '#94a3b8',
    fontSize: 12,
    marginTop: 2,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  ticketMeta: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    gap: 4,
    flexShrink: 0,
  },
  statusPill: {
    padding: '2px 8px',
    borderRadius: 100,
    fontSize: 10,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  statusOpen:   { background: 'rgba(67,233,123,0.15)', color: '#16a34a' },
  statusClosed: { background: 'rgba(245,87,108,0.12)', color: '#dc2626' },
  ticketTime: {
    color: '#cbd5e1',
    fontSize: 10,
  },
  unreadDot: {
    background: '#0118c2',
    color: '#fff',
    borderRadius: 100,
    padding: '0 5px',
    fontSize: 10,
    fontWeight: 700,
    lineHeight: '16px',
  },

  // Right panel
  thread: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    background: '#f8fafc',
    minWidth: 0,
  },
  noThread: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },
  threadHeader: {
    padding: '14px 20px',
    borderBottom: '1px solid #e2e8f0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    background: '#fff',
  },
  threadTitle: {
    color: '#1e293b',
    fontWeight: 700,
    fontSize: 15,
  },
  threadSub: {
    color: '#94a3b8',
    fontSize: 12,
    marginTop: 2,
  },
  messages: {
    flex: 1,
    overflowY: 'auto',
    padding: '16px 20px',
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  bubble: {
    maxWidth: '70%',
    display: 'flex',
    flexDirection: 'column',
  },
  bubbleLeft:  { alignSelf: 'flex-start', alignItems: 'flex-start' },
  bubbleRight: { alignSelf: 'flex-end', alignItems: 'flex-end' },
  bubbleSenderLabel: {
    color: '#94a3b8',
    fontSize: 10,
    marginBottom: 3,
  },
  bubbleBubble: {
    padding: '10px 14px',
    borderRadius: 16,
    fontSize: 14,
    lineHeight: 1.5,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-word',
  },
  bubbleBlue: {
    background: '#0118c2',
    color: '#fff',
    borderBottomRightRadius: 4,
  },
  bubbleDark: {
    background: '#fff',
    color: '#334155',
    border: '1px solid #e2e8f0',
    borderBottomLeftRadius: 4,
  },
  bubbleTime: {
    color: '#cbd5e1',
    fontSize: 10,
    marginTop: 3,
  },
  replyBar: {
    display: 'flex',
    gap: 10,
    padding: '12px 16px',
    borderTop: '1px solid #e2e8f0',
    background: '#fff',
    alignItems: 'flex-end',
  },
  replyInput: {
    flex: 1,
    background: '#f8fafc',
    border: '1px solid #e2e8f0',
    borderRadius: 12,
    color: '#1e293b',
    fontSize: 14,
    padding: '10px 14px',
    resize: 'none',
    outline: 'none',
    fontFamily: 'inherit',
    lineHeight: 1.5,
  },
  sendBtn: {
    width: 46,
    height: 46,
    borderRadius: 12,
    background: 'linear-gradient(135deg, #011ff3, #0118c2)',
    border: 'none',
    color: '#fff',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  sendBtnDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  closedBar: {
    display: 'flex',
    alignItems: 'center',
    padding: '14px 20px',
    background: '#fef2f2',
    borderTop: '1px solid #e2e8f0',
    color: '#dc2626',
    fontSize: 13,
  },
  actionBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '6px 14px',
    borderRadius: 8,
    border: 'none',
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
  },
  actionBtnDanger: {
    background: 'rgba(245,87,108,0.1)',
    color: '#dc2626',
  },
  actionBtnSuccess: {
    background: 'rgba(67,233,123,0.12)',
    color: '#16a34a',
  },
  actionBtnDisabled: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  actionErrorBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: '10px 20px',
    background: 'rgba(245,87,108,0.1)',
    borderBottom: '1px solid #e2e8f0',
    color: '#dc2626',
    fontSize: 13,
  },
  iconBtn: {
    background: 'transparent',
    border: '1px solid #e2e8f0',
    borderRadius: 8,
    color: '#64748b',
    cursor: 'pointer',
    padding: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  centered: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#94a3b8',
    padding: 32,
    textAlign: 'center',
    gap: 8,
  },
};

export default SupportInbox;
